import { useState, type ReactNode } from 'react';

import { ChevronRight, LogOut, X } from 'lucide-react';
import { motion } from 'motion/react';

import { AnimatedAvatar } from '../../primitives/AnimatedAvatar';
import { Avatar } from '../../primitives/Avatar';
import { Button } from '../../primitives/Button';
import { Drawer } from '../../primitives/Drawer';
import { IconButton } from '../../primitives/IconButton';
import { cn, varHover, motionTransition } from '../../utils';

export interface AccountMenuUser {
  displayName: string;
  email: string;
  photoURL?: string;
  initials?: string;
}

export interface AccountMenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  href?: string;
  onClick?: () => void;
  info?: ReactNode;
  /** Renders a divider line before this item */
  separator?: boolean;
  /** Highlights this item as the currently active route */
  isActive?: boolean;
}

export interface AccountMenuLabels {
  title?: string;
  close?: string;
  signOut?: string;
}

const defaultLabels: Required<AccountMenuLabels> = {
  title: 'Mi cuenta',
  close: 'Cerrar',
  signOut: 'Cerrar sesión',
};

export interface AccountMenuProps {
  user: AccountMenuUser;
  menuItems?: AccountMenuItem[];
  onSignOut?: () => void;
  isLoading?: boolean;
  renderLink?: (props: {
    href: string;
    children: ReactNode;
    className: string;
    onClick?: () => void;
  }) => ReactNode;
  labels?: AccountMenuLabels;
}

export function AccountMenu({
  user,
  menuItems = [],
  onSignOut,
  isLoading = false,
  renderLink,
  labels: labelsProp,
}: AccountMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const labels = { ...defaultLabels, ...labelsProp };

  const handleItemClick = (item: AccountMenuItem) => {
    item.onClick?.();
    if (item.href) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Trigger Button with animation */}
      <motion.button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 rounded-full p-0.5 transition-colors hover:bg-tkf-surface-muted focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        whileHover="hover"
        whileTap="tap"
        variants={varHover(1.05)}
        transition={motionTransition.hover}
      >
        <AnimatedAvatar initials={user.initials} src={user.photoURL} size="sm" />
      </motion.button>

      {/* Account Drawer */}
      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} position="right" size="sm">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-tkf-line px-4 py-3">
            <h2 className="text-lg font-semibold text-tkf-fg">{labels.title}</h2>
            <IconButton
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              aria-label={labels.close}
            >
              <X className="h-5 w-5" />
            </IconButton>
          </div>

          {/* User Info */}
          <div className="border-b border-tkf-line px-4 py-4">
            <div className="flex items-center gap-3">
              <Avatar initials={user.initials} src={user.photoURL} size="md" className="shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-tkf-fg">{user.displayName}</p>
                <p className="truncate text-sm text-tkf-fg-muted">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          {menuItems.length > 0 && (
            <nav className="flex-1 overflow-y-auto p-2">
              <ul className="space-y-1">
                {menuItems.map(item => {
                  const itemClassName = cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5',
                    'text-sm font-medium text-tkf-fg transition-colors',
                    item.isActive
                      ? 'bg-tkf-surface-muted font-semibold'
                      : 'hover:bg-tkf-surface-muted'
                  );

                  const itemContent = (
                    <>
                      {item.icon && (
                        <span className={item.isActive ? 'text-primary-600' : 'text-tkf-fg-muted'}>
                          {item.icon}
                        </span>
                      )}
                      <span className="flex-1">{item.label}</span>
                      {item.info ? (
                        <span className="text-tkf-fg-subtle">{item.info}</span>
                      ) : item.isActive ? (
                        <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                      ) : (
                        item.href && <ChevronRight className="h-4 w-4 text-tkf-fg-subtle" />
                      )}
                    </>
                  );

                  return (
                    <li key={item.key}>
                      {item.separator && (
                        <div className="my-1 border-t border-tkf-line" role="separator" />
                      )}
                      {item.href && renderLink ? (
                        renderLink({
                          href: item.href,
                          className: itemClassName,
                          onClick: () => handleItemClick(item),
                          children: itemContent,
                        })
                      ) : item.href ? (
                        <a
                          href={item.href}
                          className={itemClassName}
                          onClick={() => handleItemClick(item)}
                        >
                          {itemContent}
                        </a>
                      ) : (
                        <button
                          type="button"
                          className={cn(itemClassName, 'w-full text-left')}
                          onClick={() => handleItemClick(item)}
                        >
                          {itemContent}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          )}

          {/* Sign Out Button */}
          {onSignOut && (
            <div className="border-t border-tkf-line p-4">
              <Button variant="danger" className="w-full" onClick={onSignOut} loading={isLoading}>
                <LogOut className="mr-2 h-4 w-4" />
                {labels.signOut}
              </Button>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
}
